/* eslint-disable no-restricted-syntax */
import formatStatus from '@app/helpers/formatStatus';
import { CircleButton, Color, Text } from '@brokerbay/elements-web';
import { ListItem } from 'components';
import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import { buildCssProps } from 'utils';
import type { TimeSlotData } from '../types';
import { getTimeSlotIdByMinute } from './helpers';

const getDataType = (options: TimeSlotData) => {
  const result: string[] = [];
  if (options.selected) {
    result.push(
      options.selected === 'start' ? 'selectedStart' : 'selectedNonStart',
    );
  }
  if (options.isPast) {
    result.push('past');
  }
  if (options.isUnavailable) {
    result.push('unavailable');
  }
  if (options.isOpenHouse) {
    result.push('openHouse');
  }
  if (options.isOffer) {
    result.push('offer');
  }
  return result.join('-');
};

const getTimeSlotStyle = (
  options: Omit<TimeSlotData, 'minute' | 'text'> &
    Pick<
      TimeSelectorCellProps,
      'shouldShowRecordWarning' | 'isListingAgent' | 'isAdminWithAccess'
    >,
) => {
  if (options.selected) {
    return buildCssProps([
      ['background-color', '#227704'],
      ['border-bottom-color', '#227704'],
      ['color', options.selected === 'start' ? Color.WHITE : Color.LIGHT_GREY],
    ]);
  }
  if (options.isUnavailable) {
    if (options.isAdminWithAccess || options.isListingAgent) {
      return 'background-color: #FEA186;';
    }
    return buildCssProps([
      ['background-color', '#FEA186'],
      ['opacity', '0.5'],
      ['pointer-events', 'none'],
    ]);
  }
  if (options.isPast) {
    if (options.isAdminWithAccess || options.isListingAgent) {
      return options.shouldShowRecordWarning
        ? 'background-color: #FEA186;'
        : 'opacity: 0.5;';
    }
    return buildCssProps([
      ['background-color', '#FEA186'],
      ['opacity', '0.5'],
      ['pointer-events', 'none'],
    ]);
  }
  if (options.isOpenHouse) {
    return buildCssProps([
      ['background-color', '#6EABFE'],
      ['color', Color.BLACK],
    ]);
  }
  if (options.isOffer) {
    return 'background-color: #FFA54B;';
  }
  return '';
};

type TimeSelectorCellProps = {
  onClick: (timeSlot: TimeSlotData) => void;
  onCounterClick: (timeSlot: TimeSlotData) => void;
  timeSlot: TimeSlotData;
  shouldShowRecordWarning: boolean;
  isListingAgent: boolean;
  isAdminWithAccess: boolean;
};

const TimeSelectorCell: React.FC<TimeSelectorCellProps> = ({
  onClick,
  onCounterClick,
  timeSlot,
  shouldShowRecordWarning,
  isListingAgent,
  isAdminWithAccess,
}) => (
    <StyledListItem
      id={getTimeSlotIdByMinute(timeSlot.minute)}
      data-cy={`timeWidget-${timeSlot.text.split(' ', 2).join(' ')}`}
      datatype={getDataType(timeSlot)}
      onClick={() => onClick(timeSlot)}
      $isUnavailable={timeSlot.isUnavailable}
      $isPast={timeSlot.isPast}
      $isOffer={timeSlot.isOffer}
      $isOpenHouse={timeSlot.isOpenHouse}
      $isSelected={timeSlot.selected}
      $shouldShowRecordWarning={shouldShowRecordWarning}
      $isListingAgent={isListingAgent}
      $isAdminWithAccess={isAdminWithAccess}
    >
      <StyledTimeContainer>
        <StyledTextContainer>
          <StyledText size="large">{timeSlot.text}</StyledText>
        </StyledTextContainer>
        {timeSlot.appointments?.length && (
          <CircleButton
            dataCy={`timeSlotAppointmentCounter-${timeSlot.minute}-btn`}
            type="default"
            size="small"
            onClick={e => {
              e.stopPropagation();
              onCounterClick(timeSlot);
            }}
          >
            {timeSlot.expanded ? 'X' : timeSlot.appointments.length}
          </CircleButton>
        )}
      </StyledTimeContainer>

      {timeSlot.expanded && (
        <StyledAppointmentListContainer>
          {timeSlot.appointments!.map((appointment, index) => (
            <StyledAppointmentText key={index}>
              {appointment.coopName}
              <br />
              {appointment.coopOrgame || ''}
              <br />
              {formatStatus(appointment.status)} (
              {moment(appointment.time).format('h:mm A')} -{' '}
              {moment(appointment.timeEnd).format('h:mm A')})
            </StyledAppointmentText>
          ))}
        </StyledAppointmentListContainer>
      )}
    </StyledListItem>
  );

const StyledListItem = styled(ListItem)<{
  $isUnavailable?: true;
  $isPast?: true;
  $isOffer?: true;
  $isOpenHouse?: true;
  $isSelected?: 'start' | 'nonStart';
  $shouldShowRecordWarning: boolean;
  $isListingAgent: boolean;
  $isAdminWithAccess: boolean;
}>`
  justify-content: center;
  cursor: pointer;
  flex-wrap: wrap;
  position: relative;
  && {
    ${({
      $isUnavailable,
      $isPast,
      $isOffer,
      $isOpenHouse,
      $isSelected,
      $shouldShowRecordWarning,
      $isListingAgent,
      $isAdminWithAccess,
    }) =>
      getTimeSlotStyle({
        isUnavailable: $isUnavailable,
        isPast: $isPast,
        isOffer: $isOffer,
        isOpenHouse: $isOpenHouse,
        selected: $isSelected,
        shouldShowRecordWarning: $shouldShowRecordWarning,
        isListingAgent: $isListingAgent,
        isAdminWithAccess: $isAdminWithAccess,
      })}
  }
`;

const StyledTimeContainer = styled.div`
  display: flex;
  width: 100%;
`;

const StyledTextContainer = styled.div`
  flex-grow: 1;
`;

const StyledText = styled(Text)`
  color: inherit;
  display: block;
  text-align: center;
`;

const StyledAppointmentListContainer = styled.div`
  width: 100%;
  margin: 16px 0;
`;

const StyledAppointmentText = styled(StyledText)`
  color: inherit;
  border-bottom: 1px solid currentColor;
  &:first-child {
    border-top: 1px solid currentColor;
  }
`;

export default TimeSelectorCell;
